import React from 'react';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import numbro from 'numbro';

function isInteger(value) {
  if ([0, '0'].includes(value)) return true;
  if (!value) return false;
  return value.toString().indexOf('.') < 0;
}

const FIELD_LIST = [
  {
    code: 'WEEK_GP_TARGET',
    key: 'targetWeeklyGp',
    label: 'GP$ Target',
    adornment: '$',
    format(value) {
      if (isInteger(value)) return value;
      return numbro(value).formatCurrency({
        thousandSeparated: true,
        mantissa: 1,
      });
    },
    group: 'GP$',
    type: 'target',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },
  {
    code: 'WEEK_CST_TARGET',
    key: 'targetHourLyCustomers',
    label: 'Customers Target',
    group: 'Customers',
    type: 'target',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },
  {
    code: 'WEEK_UPT_TARGET',
    key: 'targetWeeklyUpt',
    label: 'UPT Target',
    group: 'UPT',
    type: 'target',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },

  {
    code: 'WEEK_GP_ACTUAL',
    key: 'actualWeeklyGp',
    label: 'GP$ Actual',
    adornment: '$',
    format(value) {
      if (isInteger(value)) return value;
      return numbro(value).formatCurrency({
        thousandSeparated: true,
        mantissa: 1,
      });
    },
    group: 'GP$',
    type: 'actual',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },
  {
    code: 'WEEK_CST_ACTUAL',
    key: 'actualHourLyCustomers',
    label: 'Customers Actual',
    group: 'Customers',
    type: 'actual',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },
  {
    code: 'WEEK_UPT_ACTUAL',
    key: 'actualWeeklyUpt',
    label: 'UPT Actual',
    group: 'UPT',
    type: 'actual',
    importParser(value) {
      if (!value) return;
      return Number(value).toFixed(1);
    },
  },
  {
    code: 'WEEK_WAGE_TARGET',
    key: 'targetWagePercent',
    label: 'Wage Target',
    adornment: '$',
    format(value) {
      let pct = value;
      if (isInteger(value)) pct = value;
      else {
        pct = Number(value).toFixed(1);
      }
      return pct + '%';
    },
    importParser(value) {
      if (!value) return;
      return (Number(value) * 100).toFixed(1);
    },
    group: 'Wage',
    type: 'target',
  },
  {
    code: 'WEEK_WAGE_ACTUAL',
    key: 'actualWagePercent',
    label: 'Wage Actual',
    adornment: '$',
    format(value) {
      let pct = value;
      if (isInteger(value)) pct = value;
      else {
        pct = Number(value).toFixed(1);
      }
      return pct + '%';
    },
    importParser(value) {
      if (!value) return;
      return (Number(value) * 100).toFixed(1);
    },
    group: 'Wage',
    type: 'actual',
  },
];

export const TARGET_FIELDS = keyBy(FIELD_LIST, 'code');
export const TARGET_FIELDS_BY_KEY = keyBy(FIELD_LIST, 'key');

export const KPI_STATUS_TYPE = {
  POSITIVE: {
    color: 'green',
  },
  NEGATIVE: {
    color: 'red',
  },
};

function evalKpiResult(targetData, key1, key2) {
  const dataKey1 = TARGET_FIELDS[key1].key;
  const dataKey2 = TARGET_FIELDS[key2].key;

  if (Number(get(targetData, [dataKey1], 0)) >= Number(get(targetData, [dataKey2], 0))) {
    return {
      [key1]: 'POSITIVE',
      [key2]: 'POSITIVE',
    };
  } else {
    return {
      [key1]: 'NEGATIVE',
      [key2]: 'NEGATIVE',
    };
  }
}

export default function usePprStats(pprData) {
  const status = React.useMemo(() => {
    let kpiStatus = {};
    if (pprData) {
      kpiStatus = {
        ...evalKpiResult(pprData, 'WEEK_GP_ACTUAL', 'WEEK_GP_TARGET'),
        ...evalKpiResult(pprData, 'WEEK_CST_ACTUAL', 'WEEK_CST_TARGET'),
        ...evalKpiResult(pprData, 'WEEK_UPT_ACTUAL', 'WEEK_UPT_TARGET'),
      };
    }
    return kpiStatus;
  }, [pprData]);
  const kpiByKey = React.useMemo(() => {
    const statsByKey = {};
    Object.entries(status).forEach(([code, values]) => {
      statsByKey[TARGET_FIELDS[code].key] = values;
    });
    return statsByKey;
  }, [status]);
  return { kpiByCode: status, kpiByKey };
}
